'use client';

import { useState, useEffect } from 'react';

interface Section {
  id: string;
  label: string;
}

interface SectionProgressIndicatorProps {
  className?: string;
}

const SectionProgressIndicator = ({
  className = '',
}: SectionProgressIndicatorProps) => {
  const [activeSection, setActiveSection] = useState('');

  const sections: Section[] = [
    { id: 'products', label: 'Products' },
    { id: 'our-story', label: 'Our Story' },
    { id: 'why-choose-us', label: 'Why Choose Us' },
    { id: 'reviews', label: 'Reviews' },
    { id: 'recipes', label: 'Recipes' },
    { id: 'faq', label: 'FAQ' },
  ];

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-50% 0px -50% 0px',
      threshold: 0,
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions
    );

    sections.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      sections.forEach((section) => {
        const element = document.getElementById(section.id);
        if (element) {
          observer.unobserve(element);
        }
      });
    };
  }, []);

  const handleSectionClick = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div
      className={`fixed left-6 top-1/2 z-[50] hidden -translate-y-1/2 lg:block ${className}`}
    >
      <div className="flex flex-col gap-4">
        {sections.map((section, index) => (
          <button
            key={section.id}
            onClick={() => handleSectionClick(section.id)}
            className="group relative flex items-center"
            aria-label={`Go to ${section.label}`}
          >
            <div
              className={`h-3 w-3 rounded-full border-2 transition-all duration-250 ease-out ${
                activeSection === section.id
                  ? 'scale-125 border-primary bg-primary' :'border-muted-foreground bg-background hover:border-primary hover:bg-primary/20'
              }`}
            />
            <div
              className={`absolute left-6 whitespace-nowrap rounded-lg bg-popover px-3 py-1.5 text-sm font-medium text-popover-foreground shadow-cta opacity-0 transition-opacity duration-250 ease-out group-hover:opacity-100 ${
                activeSection === section.id ? 'opacity-100' : ''
              }`}
            >
              {section.label}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default SectionProgressIndicator;